<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable */
import { computed, inject, proxyRefs, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});

const emit = defineEmits(['navigate']);
const emptyRuntime = {
  actions: { proposals: {} },
  data: ref({}),
  error: ref(''),
  loading: ref(false),
  refresh: () => {},
  saving: ref(false),
  selectedId: ref(''),
};
const realData = inject('imudeiRealData', null) || emptyRuntime;
const { error, loading, refresh } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'proposals'),
    screen: computed(() => 'dashboard'),
  })
);

const searchQuery = ref('');
const activeStatus = ref('all');
const purposeFilter = ref('all');
const propertyTypeFilter = ref('all');
const paymentFilter = ref('all');
const periodFilter = ref('all');
const selectedId = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const sourceOf = record => record?.source || record || {};
const valueAt = (record, path) =>
  path.split('.').reduce((value, key) => value?.[key], record);
const firstValue = (record, paths, fallback = '') => {
  const value = paths
    .map(path => valueAt(record, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return value ?? fallback;
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const readable = value => {
  const text = String(value || '').replaceAll('_', ' ').trim();
  return text
    ? `${text[0].toLocaleUpperCase('pt-BR')}${text.slice(1)}`
    : 'Indisponível';
};
const money = (value, currency) => {
  if (value === undefined || value === null || value === '') {
    return 'Indisponível';
  }

  const amount = typeof value === 'object' ? value.amount ?? value.value : value;
  const number = Number(amount);
  if (!Number.isFinite(number)) return String(amount);

  const normalizedCurrency = String(currency || '').toUpperCase();
  if (!/^[A-Z]{3}$/.test(normalizedCurrency)) {
    return new Intl.NumberFormat('pt-BR').format(number);
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: normalizedCurrency,
    maximumFractionDigits: 2,
  }).format(number);
};
const relativeDate = value => {
  if (!value) return 'Data indisponível';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  const hours = Math.floor((Date.now() - parsed.valueOf()) / 3600000);
  if (hours < 1) return 'Agora';
  if (hours < 24) return `Há ${hours}h`;
  return `Há ${Math.floor(hours / 24)} dias`;
};
const dateLabel = value => {
  if (!value) return 'Não retornada';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR').format(parsed);
};
const statusGroup = status => {
  const value = String(status || '').toLocaleLowerCase('pt-BR');
  if (/doc|kyc|cr[ée]dito/.test(value)) return 'Documentação';
  if (/contract|contrat|assin/.test(value)) return 'Contrato';
  if (/declin|withdraw|cancel|recus|discard/.test(value)) return 'Canceladas';
  if (/final|aceit|conclu|approved/.test(value)) return 'Finalizadas';
  return 'Negociação';
};
const statusTone = status => {
  const group = statusGroup(status);
  if (group === 'Finalizadas') return 'bg-emerald-50 text-emerald-700';
  if (group === 'Canceladas') return 'bg-rose-50 text-rose-700';
  if (group === 'Documentação') return 'bg-sky-50 text-sky-700';
  if (group === 'Contrato') return 'bg-orange-50 text-orange-700';
  return 'bg-violet-50 text-[#5a24c7]';
};
const stageTone = stage => {
  const state = String(stage?.state || stage?.status || '').toLowerCase();
  if (stage?.current || /atual|current|andamento|in_progress/.test(state)) {
    return 'border-[#5a24c7] bg-[#5a24c7]';
  }
  if (/concl|complet|done|final/.test(state)) {
    return 'border-emerald-500 bg-emerald-500';
  }
  return 'border-[#c3c8da] bg-white';
};
const stageLabelTone = stage => {
  const state = String(stage?.state || stage?.status || '').toLowerCase();
  if (stage?.current || /atual|current|andamento|in_progress/.test(state)) {
    return 'font-bold text-[#5a24c7]';
  }
  if (/concl|complet|done|final/.test(state)) return 'text-emerald-600';
  return 'text-[#7883a2]';
};
const stageConnectorTone = stage => {
  const state = String(stage?.state || stage?.status || '').toLowerCase();
  return stage?.current || /concl|complet|done|final|atual|current/.test(state)
    ? 'bg-[#5a24c7]'
    : 'bg-[#dce0ec]';
};
const messageFrom = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(' ');
  return value.message || value.error || value.errors?.[0] || '';
};

const runtimeRecords = computed(() =>
  props.records.length ? props.records : asArray(model.records)
);
const runtimeMetrics = computed(() =>
  props.metrics.length ? props.metrics : asArray(model.metrics)
);
const cards = computed(() =>
  runtimeRecords.value.map(record => {
    const source = sourceOf(record);
    const revision = source.current_revision || source.currentRevision || {};
    const rawStatus = firstValue(
      source,
      ['macro_status_label', 'status_label', 'status', 'macro_status'],
      record.status
    );
    const client = firstValue(
      source,
      [
        'contact.name',
        'contact.full_name',
        'contact_name',
        'client.name',
        'buyer.name',
      ],
      'Cliente indisponível'
    );
    const broker = firstValue(
      source,
      ['user.name', 'user_name', 'broker.name', 'responsible.name'],
      'Responsável indisponível'
    );
    const title =
      record.title ||
      firstValue(
        source,
        [
          'imovel.titulo',
          'imovel.title',
          'imovel_titulo',
          'property.title',
          'listing.title',
        ],
        'Imóvel indisponível'
      );
    const address = firstValue(
      source,
      [
        'imovel.endereco',
        'imovel.address',
        'imovel_endereco',
        'property.address',
        'listing.address',
      ],
      'Endereço indisponível'
    );
    const amountValue = firstValue(
      revision,
      ['offer_amount', 'amount', 'value'],
      firstValue(source, ['offer_amount', 'amount', 'value'])
    );
    const updatedAt = firstValue(source, [
      'updated_at',
      'created_at',
      'submitted_at',
    ]);
    const purpose = readable(
      firstValue(source, [
        'imovel.finalidade',
        'imovel_finalidade',
        'property.purpose',
        'purpose',
        'finalidade',
      ])
    );
    const payment = readable(
      firstValue(
        revision,
        ['payment_method_label', 'payment_method', 'payment_type_label', 'payment_type'],
        firstValue(source, [
          'payment_method_label',
          'payment_method',
          'payment_type_label',
          'payment_type',
        ])
      )
    );

    return {
      address,
      age: relativeDate(
        firstValue(source, ['updated_at', 'created_at', 'submitted_at'])
      ),
      amount: record.amount || money(amountValue, revision.currency || source.currency),
      broker,
      category: statusGroup(rawStatus),
      client,
      id: String(record.id || source.id || ''),
      image: firstValue(source, [
        'imovel.cover_url',
        'imovel.image_url',
        'property.cover_url',
        'property.image_url',
        'listing.cover_url',
        'listing.image_url',
        'image_url',
      ]),
      propertyType: readable(
        firstValue(
          source,
          ['imovel.tipo', 'imovel.type', 'imovel_finalidade', 'property.type', 'kind'],
          ''
        )
      ),
      purpose,
      payment,
      source,
      status: readable(rawStatus),
      title,
      updatedAt,
      validUntil: dateLabel(
        firstValue(
          revision,
          ['valid_until', 'expires_at', 'expires_on', 'validity_date'],
          firstValue(source, [
            'valid_until',
            'expires_at',
            'expires_on',
            'validity_date',
          ])
        )
      ),
    };
  })
);
const statusTabs = computed(() => {
  const groups = new Map();
  cards.value.forEach(card => {
    groups.set(card.category, (groups.get(card.category) || 0) + 1);
  });

  return [
    { count: cards.value.length, key: 'all', label: 'Todas' },
    ...Array.from(groups, ([label, count]) => ({ count, key: label, label })),
  ];
});
const valuesFor = field =>
  [...new Set(cards.value.map(card => card[field]))]
    .filter(value => value && value !== 'Indisponível')
    .sort((left, right) => left.localeCompare(right, 'pt-BR'));
const purposeOptions = computed(() => valuesFor('purpose'));
const propertyTypeOptions = computed(() => valuesFor('propertyType'));
const paymentOptions = computed(() => valuesFor('payment'));
const matchesPeriod = card => {
  if (periodFilter.value === 'all') return true;

  const updatedAt = new Date(card.updatedAt);
  if (Number.isNaN(updatedAt.valueOf())) return false;

  const days = Number(periodFilter.value);
  return Date.now() - updatedAt.valueOf() <= days * 24 * 60 * 60 * 1000;
};
const filteredCards = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  return cards.value
    .filter(card => activeStatus.value === 'all' || card.category === activeStatus.value)
    .filter(card => purposeFilter.value === 'all' || card.purpose === purposeFilter.value)
    .filter(
      card =>
        propertyTypeFilter.value === 'all' ||
        card.propertyType === propertyTypeFilter.value
    )
    .filter(card => paymentFilter.value === 'all' || card.payment === paymentFilter.value)
    .filter(matchesPeriod)
    .filter(card => {
      if (!query) return true;
      return [
        card.title,
        card.address,
        card.client,
        card.broker,
        card.status,
        card.propertyType,
      ]
        .join(' ')
        .toLocaleLowerCase('pt-BR')
        .includes(query);
    });
});
const dashboardStageOrder = ['Negociação', 'Documentação', 'Contrato'];
const categoryIcon = category => {
  const icons = {
    Canceladas: 'i-lucide-circle-x',
    Contrato: 'i-lucide-pen-line',
    Documentação: 'i-lucide-file-text',
    Finalizadas: 'i-lucide-check',
    Negociação: 'i-lucide-handshake',
  };

  return icons[category] || 'i-lucide-file-text';
};
const categoryIconTone = category => {
  const tones = {
    Canceladas: 'bg-rose-50 text-rose-600',
    Contrato: 'bg-orange-50 text-orange-600',
    Documentação: 'bg-violet-50 text-[#5a24c7]',
    Finalizadas: 'bg-emerald-50 text-emerald-600',
    Negociação: 'bg-violet-50 text-[#5a24c7]',
  };

  return tones[category] || 'bg-violet-50 text-[#5a24c7]';
};
const visibleLaneCategories = computed(() =>
  activeStatus.value === 'all' ? dashboardStageOrder : [activeStatus.value]
);
const boardLanes = computed(() =>
  visibleLaneCategories.value.map(category => {
    const laneCards = filteredCards.value.filter(card => card.category === category);

    return {
      cards: laneCards.slice(0, 3),
      category,
      count: laneCards.length,
      id: category.toLocaleLowerCase('pt-BR').replaceAll(' ', '-'),
      remaining: Math.max(0, laneCards.length - 3),
    };
  })
);
const displayedCards = computed(() =>
  boardLanes.value.flatMap(lane => lane.cards)
);
const selected = computed(
  () => cards.value.find(card => card.id === selectedId.value) || null
);
const dashboardMetrics = computed(() => runtimeMetrics.value.slice(0, 4));
const visibleTotal = computed(() => {
  const total = Number(props.total);
  return Number.isFinite(total) && total > 0 ? total : cards.value.length;
});
const selectedTimeline = computed(() => {
  const source = selected.value?.source || {};
  const timeline = [
    source.timeline,
    source.timeline_payload?.steps,
    source.negotiation_timeline,
  ].find(Array.isArray);

  return asArray(timeline)
    .slice(0, 4)
    .map((stage, index) => ({
      current:
        stage?.current === true ||
        `${stage?.key || stage?.id || ''}` ===
          `${source.current_step?.key || source.current_step || source.sales_flow_step || ''}`,
      id: String(stage?.id || stage?.key || index),
      label: readable(
        firstValue(stage, ['label', 'title', 'name', 'key', 'status'], '')
      ),
      state: firstValue(stage, ['state', 'status'], ''),
    }));
});
const activities = computed(() => {
  const source = selected.value?.source || {};
  const sourceHistory = asArray(source.negotiation_timeline);
  const selectedRuntimeHistory =
    `${model.detail?.id || ''}` === `${selected.value?.id || ''}`
      ? props.history.length
        ? props.history
        : asArray(model.history)
      : [];
  const history = sourceHistory.length ? sourceHistory : selectedRuntimeHistory;

  return asArray(history)
    .slice(0, 4)
    .map((event, index) => ({
      actor: firstValue(
        event,
        ['actor.name', 'user.name', 'author.name', 'responsible.name'],
        'Autor indisponível'
      ),
      date: relativeDate(
        firstValue(event, ['occurred_at', 'created_at', 'updated_at', 'at'])
      ),
      id: String(event?.id || event?.sequence || index),
      label: readable(
        firstValue(
          event,
          ['label', 'action', 'event_type', 'tipo', 'notes', 'reason', 'status_novo'],
          'Atualização sem descrição'
        )
      ),
    }));
});
const canCreate = computed(() => model.canCreate === true);
const canEdit = computed(() => {
  const permissions = selected.value?.source?.permissions || selected.value?.source?.actions;
  if (permissions && Object.keys(permissions).length) {
    return permissions.can_update === true || permissions.update === true;
  }

  return model.canEdit === true;
});
const unavailableCopy = computed(() =>
  props.preview
    ? 'Dados não carregados na visualização de referência.'
    : 'Nenhuma proposta foi carregada para este filtro.'
);
const apiError = computed(() => messageFrom(error?.value || error) || model.localError);

watch(
  cards,
  list => {
    if (!list.some(card => card.id === selectedId.value)) {
      selectedId.value = list.find(card => card.id)?.id || '';
    }
  },
  { immediate: true }
);
watch(statusTabs, tabs => {
  if (!tabs.some(tab => tab.key === activeStatus.value)) activeStatus.value = 'all';
});
watch(filteredCards, list => {
  if (!list.some(card => card.id === selectedId.value)) {
    selectedId.value = list.find(card => card.id)?.id || '';
  }
});

const select = card => {
  if (card?.id) selectedId.value = card.id;
};
const open = screen => {
  if (selected.value?.id) emit('navigate', { id: selected.value.id, screen });
};
const openCard = (card, screen) => {
  select(card);
  if (card?.id) emit('navigate', { id: card.id, screen });
};
const reload = () => refresh?.();
const applyFilters = () => {
  if (!filteredCards.value.some(card => card.id === selectedId.value)) {
    selectedId.value = filteredCards.value.find(card => card.id)?.id || '';
  }
};
const clearFilters = () => {
  searchQuery.value = '';
  activeStatus.value = 'all';
  purposeFilter.value = 'all';
  propertyTypeFilter.value = 'all';
  paymentFilter.value = 'all';
  periodFilter.value = 'all';
  applyFilters();
};
const selectedAchievements = computed(() => {
  const source = selected.value?.source || {};
  const achievements = firstValue(source, [
    'achievements',
    'gamification.achievements',
    'gamification.rewards',
    'rewards',
  ], []);

  return asArray(achievements)
    .slice(0, 2)
    .map((achievement, index) => ({
      id: String(achievement?.id || achievement?.key || index),
      label: readable(
        firstValue(achievement, ['label', 'title', 'name', 'description'])
      ),
    }));
});
</script>

<template>
  <main class="w-full pb-4 pl-10 pr-[14px] pt-4 text-[#101958]">
    <header data-imudei-visual-id="proposal-dashboard-header" class="flex h-[83px] items-start justify-between pr-[30px]">
      <div>
        <h1 class="text-[25px] font-bold tracking-[-0.035em]">Propostas</h1>
        <p class="mt-1 text-[10px] text-[#66739b]">
          Acompanhe e gerencie todas as propostas em andamento.
        </p>
      </div>
      <button
        v-if="canCreate"
        class="rounded-xl bg-gradient-to-r from-[#5522bf] to-[#a82adf] px-5 py-2.5 text-[9px] font-bold text-white"
        type="button"
        @click="emit('navigate', { screen: 'create' })"
      >
        <i class="i-lucide-plus mr-1 inline size-3" />Nova proposta
      </button>
      <span
        v-else
        class="rounded-xl border border-[#e3dcef] px-4 py-2.5 text-[8px] text-[#7180a4]"
      >
        Criação indisponível para suas permissões
      </span>
    </header>

    <p
      v-if="apiError"
      class="mb-4 flex items-center justify-between rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] text-rose-700"
    >
      <span>{{ apiError }}</span>
      <button class="font-bold underline" type="button" @click="reload">
        Tentar novamente
      </button>
    </p>

    <section data-imudei-visual-id="proposal-dashboard-metrics" class="grid max-w-[950px] grid-cols-4 gap-3">
      <article
        v-for="metric in dashboardMetrics"
        :key="metric.label"
        class="h-[91px] min-h-0 rounded-2xl border border-[#e8e4ef] bg-white px-4 py-3 shadow-[0_3px_16px_rgba(54,35,100,0.035)]"
      >
        <div class="flex items-start justify-between">
          <span>
            <small class="block text-[9px] text-[#7180a4]">{{ metric.label }}</small>
            <strong class="mt-2 block text-[20px] font-bold text-[#101958]">{{ metric.value }}</strong>
          </span>
          <i
            :class="metric.icon || 'i-lucide-chart-no-axes-column-increasing'"
            class="grid size-8 place-items-center rounded-xl bg-[#f2ecff] text-[#5b20d2]"
          />
        </div>
        <small class="mt-3 block text-[8px] text-[#7883a2]">Dados carregados da lista atual</small>
      </article>
      <article
        v-for="slot in Math.max(0, 4 - dashboardMetrics.length)"
        :key="`metric-unavailable-${slot}`"
        class="h-[91px] min-h-0 rounded-2xl border border-dashed border-[#e8e4ef] bg-[#fcfbfe] px-4 py-3"
      >
        <small class="block text-[9px] text-[#7180a4]">Métrica indisponível</small>
        <strong class="mt-2 block text-[20px] font-bold text-[#c3c8da]">—</strong>
        <small class="mt-3 block text-[8px] text-[#7883a2]">Sem dado correspondente no contrato</small>
      </article>
    </section>

    <div class="mt-2 grid grid-cols-[minmax(0,1fr)_448px] gap-[30px]">
      <section class="pt-2">
        <form data-imudei-visual-id="proposal-dashboard-filters" class="grid h-[76px] grid-cols-[repeat(4,minmax(0,0.9fr))_minmax(0,1.45fr)_minmax(0,1.15fr)_auto_auto] gap-2 overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white px-4 pb-4 pt-3 shadow-[0_3px_16px_rgba(54,35,100,0.035)]" @submit.prevent="applyFilters">
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Finalidade</small>
            <select v-model="purposeFilter" class="h-7 w-full rounded-lg border border-[#e6e1ee] bg-white px-2 text-[8px] text-[#101958] outline-none">
              <option value="all">Todos</option>
              <option v-for="purpose in purposeOptions" :key="purpose" :value="purpose">{{ purpose }}</option>
            </select>
          </label>
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Situação</small>
            <select v-model="activeStatus" class="h-7 w-full rounded-lg border border-[#e6e1ee] bg-white px-2 text-[8px] text-[#101958] outline-none">
              <option value="all">Todos</option>
              <option v-for="tab in statusTabs.filter(tab => tab.key !== 'all')" :key="tab.key" :value="tab.key">{{ tab.label }}</option>
            </select>
          </label>
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Tipo</small>
            <select v-model="propertyTypeFilter" class="h-7 w-full rounded-lg border border-[#e6e1ee] bg-white px-2 text-[8px] text-[#101958] outline-none">
              <option value="all">Todos</option>
              <option v-for="propertyType in propertyTypeOptions" :key="propertyType" :value="propertyType">{{ propertyType }}</option>
            </select>
          </label>
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Pagamento</small>
            <select v-model="paymentFilter" class="h-7 w-full rounded-lg border border-[#e6e1ee] bg-white px-2 text-[8px] text-[#101958] outline-none">
              <option value="all">Todos</option>
              <option v-for="payment in paymentOptions" :key="payment" :value="payment">{{ payment }}</option>
            </select>
          </label>
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Período</small>
            <select v-model="periodFilter" class="h-7 w-full rounded-lg border border-[#e6e1ee] bg-white px-2 text-[8px] text-[#101958] outline-none">
              <option value="all">Todos</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </label>
          <label class="min-w-0">
            <small class="mb-1 block text-[7px] text-[#7180a4]">Cliente</small>
            <span class="flex h-7 items-center gap-2 rounded-lg border border-[#e6e1ee] px-2">
              <input v-model="searchQuery" class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[8px] outline-none placeholder:text-[#a4acc1]" placeholder="Buscar cliente..." type="search" />
              <i class="i-lucide-search size-3 text-[#7180a4]" />
            </span>
          </label>
          <button class="mt-[15px] h-7 rounded-lg border border-[#e8e4ef] px-3 text-[8px] font-semibold text-[#5a24c7]" type="button" @click="clearFilters">Limpar filtros</button>
          <button class="mt-[15px] h-7 rounded-lg bg-gradient-to-r from-[#5620ce] to-[#7828ec] px-3 text-[8px] font-semibold text-white" type="submit">
            <i class="i-lucide-sliders-horizontal mr-1 inline-block size-3" />
            Filtrar
          </button>
        </form>

        <div class="mt-4 flex items-center gap-2 overflow-x-auto border-b border-[#eeeaf4]">
          <button
            v-for="tab in statusTabs"
            :key="tab.key"
            class="border-b-2 px-2 pb-3 text-[9px] font-semibold whitespace-nowrap"
            :class="activeStatus === tab.key ? 'border-[#5a24c7] text-[#5a24c7]' : 'border-transparent text-[#7883a2]'"
            type="button"
            @click="activeStatus = tab.key"
          >
            {{ tab.label }}
            <span class="ml-1 rounded-full bg-[#f3f0f8] px-1.5 py-0.5 text-[7px]">{{ tab.count }}</span>
          </button>
        </div>

        <p class="sr-only">
          Busca e filtro aplicados somente às propostas reais carregadas.
        </p>

        <div data-imudei-visual-id="proposal-dashboard-board" class="mt-2.5 grid h-[666px] grid-cols-3 gap-3">
          <section
            v-for="lane in boardLanes"
            :key="lane.id"
            :data-imudei-proposal-lane="lane.category"
            class="flex min-w-0 flex-col"
          >
            <header class="flex h-9 items-center justify-between px-3">
              <span class="flex min-w-0 items-center gap-2">
                <span
                  class="grid size-7 shrink-0 place-items-center rounded-full"
                  :class="categoryIconTone(lane.category)"
                >
                  <i :class="categoryIcon(lane.category)" class="size-3.5" />
                </span>
                <strong class="truncate text-[10px]">{{ lane.category }}</strong>
                <small class="rounded-full bg-violet-50 px-1.5 py-0.5 text-[8px] font-bold text-[#5a24c7]">
                  {{ lane.count }}
                </small>
              </span>
              <i class="i-lucide-ellipsis-vertical size-4 shrink-0 text-[#172050]" />
            </header>

            <div v-if="lane.cards.length" class="space-y-3">
              <article
                v-for="card in lane.cards"
                :key="card.id || `${card.title}-${card.client}`"
                class="group h-[190px] min-h-0 rounded-2xl border border-[#e8e4ef] bg-white p-3 shadow-[0_3px_16px_rgba(54,35,100,0.035)] transition hover:border-[#d8c8ff]"
                :class="card.id === selectedId ? 'ring-1 ring-[#a982ff]' : ''"
              >
                <button class="flex w-full items-start gap-2 text-left" type="button" @click="select(card)">
                  <span class="grid size-9 shrink-0 place-items-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">{{ initials(card.client) }}</span>
                  <span class="min-w-0 flex-1">
                    <span class="flex items-start gap-2">
                      <strong class="min-w-0 flex-1 truncate text-[10px]">{{ card.client }}</strong>
                      <small class="shrink-0 rounded-md bg-violet-50 px-1.5 py-0.5 text-[7px] font-semibold text-[#5a24c7]">{{ card.age }}</small>
                    </span>
                    <small class="mt-1 block truncate text-[7px] text-[#68759b]">{{ card.propertyType }} <span aria-hidden="true">·</span> {{ card.title }}</small>
                    <small class="mt-1 block truncate text-[7px] text-[#7883a2]"><i class="i-lucide-map-pin mr-1 inline-block size-2.5" />{{ card.address }}</small>
                  </span>
                  <span class="relative grid size-[52px] shrink-0 place-items-center overflow-hidden rounded-lg bg-[#f6f3fb] text-[#5a24c7]">
                    <img v-if="card.image" :src="card.image" :alt="card.title" class="h-full w-full object-cover" />
                    <i v-else class="i-lucide-house size-4" />
                    <i class="absolute right-1 top-1 i-lucide-arrow-up-right size-3 rounded-md bg-white/90 text-[#5a24c7]" />
                  </span>
                </button>
                <div class="mt-3 grid grid-cols-2 gap-3 border-t border-[#eeeaf4] pt-2">
                  <span>
                    <small class="block text-[7px] text-[#7883a2]">Valor proposto</small>
                    <strong class="mt-0.5 block truncate text-[12px] text-[#5a24c7]">{{ card.amount }}</strong>
                  </span>
                  <span class="min-w-0 border-l border-[#eeeaf4] pl-3">
                    <small class="block text-[7px] text-[#7883a2]">Corretor responsável</small>
                    <strong class="mt-0.5 block truncate text-[8px]">{{ card.broker }}</strong>
                  </span>
                </div>
                <footer class="mt-2 grid grid-cols-3 gap-2 border-t border-[#eeeaf4] pt-2">
                  <button
                    class="h-6 rounded-lg border border-[#d7c8ff] text-[7px] font-bold text-[#5a24c7]"
                    type="button"
                    :disabled="!card.id"
                    @click="openCard(card, 'detail')"
                  >
                    Ver proposta
                  </button>
                  <button
                    v-if="canEdit"
                    class="h-6 rounded-lg border border-[#d7c8ff] text-[7px] font-bold text-[#5a24c7]"
                    type="button"
                    :disabled="!card.id"
                    @click="openCard(card, 'edit')"
                  >
                    Editar
                  </button>
                  <span v-else class="grid h-6 place-items-center rounded-lg bg-[#f5f3f8] px-1 text-center text-[7px] text-[#7180a4]">
                    Edição indisponível
                  </span>
                  <span class="grid h-6 place-items-center rounded-lg bg-[#f5f3f8] px-1 text-center text-[7px] text-[#7180a4]">
                    Ações nos detalhes
                  </span>
                </footer>
              </article>
            </div>

            <article
              v-else
              class="grid h-[190px] place-items-center rounded-2xl border border-dashed border-[#e8e4ef] bg-[#fcfbfe] p-4 text-center"
            >
              <span>
                <i class="i-lucide-folder-open-dot mx-auto size-6 text-[#a58add]" />
                <strong class="mt-3 block text-[9px]">Nenhuma proposta nesta etapa</strong>
                <small class="mt-1 block text-[7px] text-[#7883a2]">{{ unavailableCopy }}</small>
              </span>
            </article>

            <button
              v-if="lane.remaining"
              class="mt-auto flex h-9 items-center justify-center gap-2 rounded-xl border border-dashed border-[#ddd5ea] text-[9px] font-bold text-[#5a24c7]"
              type="button"
              @click="activeStatus = lane.category"
            >
              <i class="i-lucide-plus size-3.5" /> Ver todas ({{ lane.count }})
            </button>
          </section>
        </div>

        <p class="mt-4 text-[9px] text-[#66739b]">
          {{ displayedCards.length }} de {{ visibleTotal }} propostas reais exibidas
        </p>
      </section>

      <aside data-imudei-visual-id="proposal-dashboard-detail" class="-mt-[117px] flex min-h-[928px] flex-col rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_3px_16px_rgba(54,35,100,0.035)]">
        <template v-if="selected">
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Visualização rápida da proposta</h2>
            <button
              class="i-lucide-x size-4 text-[#5d6687]"
              type="button"
              aria-label="Fechar visualização"
              @click="selectedId = ''"
            />
          </header>

          <div class="mt-4 flex gap-3">
            <span class="grid h-[112px] w-[188px] shrink-0 place-items-center overflow-hidden rounded-xl bg-violet-50 text-[#5a24c7]">
              <img v-if="selected.image" :src="selected.image" :alt="selected.title" class="h-full w-full object-cover" />
              <i v-else class="i-lucide-house size-7" />
            </span>
            <span class="min-w-0">
              <small class="rounded bg-violet-50 px-2 py-1 text-[8px] text-[#5a24c7]">{{ selected.id }}</small>
              <b class="mt-3 block text-[11px]">{{ selected.title }}</b>
              <small class="mt-1 block text-[8px] text-[#7180a4]">{{ selected.address }}</small>
              <small class="mt-3 block text-[8px]">{{ selected.propertyType }}</small>
            </span>
          </div>

          <div class="mt-4 flex items-center gap-3 border-y border-[#eeeaf4] py-3">
            <span class="grid size-9 place-items-center rounded-full bg-rose-100 text-[9px] font-bold text-rose-700">{{ initials(selected.client) }}</span>
            <span class="min-w-0">
              <small class="block text-[8px] text-[#7180a4]">Cliente</small>
              <b class="block truncate text-[9px]">{{ selected.client }}</b>
            </span>
          </div>

          <div class="mt-0 grid min-h-[113px] grid-cols-2 gap-3 rounded-xl border border-[#e8e4ef] p-3">
            <span>
              <small class="block text-[8px] text-[#7180a4]">Valor proposto</small>
              <b class="mt-2 block text-[14px] text-[#5a24c7]">{{ selected.amount }}</b>
            </span>
            <span class="border-l border-[#eeeaf4] pl-3">
              <small class="block text-[8px] text-[#7180a4]">Situação</small>
              <b class="mt-2 block text-[11px]">{{ selected.status }}</b>
            </span>
            <span class="border-t border-[#eeeaf4] pt-3">
              <small class="block text-[8px] text-[#7180a4]">Condição de pagamento</small>
              <b class="mt-2 block text-[10px]">{{ selected.payment }}</b>
            </span>
            <span class="border-l border-t border-[#eeeaf4] pl-3 pt-3">
              <small class="block text-[8px] text-[#7180a4]">Validade da proposta</small>
              <b class="mt-2 block text-[10px]">{{ selected.validUntil }}</b>
            </span>
          </div>

          <h3 class="mt-3 text-[10px] font-bold">Progresso da proposta</h3>
          <div v-if="selectedTimeline.length" class="mt-4 flex">
            <span
              v-for="(stage, index) in selectedTimeline"
              :key="stage.id"
              class="relative flex flex-1 flex-col items-center text-center"
            >
              <i class="z-10 grid size-4 place-items-center rounded-full border" :class="stageTone(stage)" />
              <i
                v-if="index < selectedTimeline.length - 1"
                class="absolute left-1/2 top-2 h-px w-full"
                :class="stageConnectorTone(stage)"
              />
              <small class="mt-2 text-[7px]" :class="stageLabelTone(stage)">{{ stage.label }}</small>
            </span>
          </div>
          <div v-else class="mt-4 rounded-xl bg-[#faf9fd] px-3 py-4 text-[8px] text-[#7180a4]">
            Cronograma indisponível para esta proposta.
          </div>

          <h3 class="mt-4 text-[10px] font-bold">Ação recomendada</h3>
          <div class="mt-3 grid grid-cols-[1.25fr_.75fr] gap-3">
            <article class="rounded-xl border border-violet-100 bg-gradient-to-br from-[#fbf5ff] to-[#f2ecff] p-3">
              <small class="text-[8px] font-bold">Próxima ação</small>
              <strong class="mt-3 block text-[9px]">Consulte as ações permitidas nos detalhes.</strong>
              <button
                class="mt-3 w-full rounded-lg bg-gradient-to-r from-[#5620ce] to-[#7828ec] px-2 py-2 text-[8px] font-bold text-white"
                type="button"
                @click="open('detail')"
              >
                Abrir detalhes
              </button>
            </article>
            <article class="rounded-xl bg-[#faf9fd] p-3">
              <small class="text-[8px] font-bold">Recompensas</small>
              <strong class="mt-3 block text-[10px] text-[#5a24c7]">Indisponível</strong>
              <small class="mt-2 block text-[7px] text-[#7180a4]">Sem endpoint de gamificação.</small>
            </article>
          </div>

          <h3 class="mt-5 text-[10px] font-bold">Atividade recente</h3>
          <div
            v-for="activity in activities"
            :key="activity.id"
            class="mt-3 flex items-center gap-2 border-b border-[#eeeaf4] pb-3"
          >
            <span class="grid size-7 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">{{ initials(activity.actor) }}</span>
            <span class="min-w-0 flex-1">
              <b class="block truncate text-[8px]">{{ activity.actor }}</b>
              <small class="block truncate text-[7px] text-[#7883a2]">{{ activity.label }}</small>
            </span>
            <small class="text-[7px] text-[#7883a2]">{{ activity.date }}</small>
          </div>
          <p v-if="!activities.length" class="mt-4 rounded-xl bg-[#faf9fd] px-3 py-3 text-[8px] text-[#7180a4]">
            Atividade indisponível para esta proposta.
          </p>

          <section class="mt-auto border-t border-[#eeeaf4] pt-3">
            <div class="flex items-center justify-between gap-3">
              <h3 class="text-[9px] font-bold">Conquistas desta proposta</h3>
              <small class="text-[7px] text-[#7180a4]">{{ selectedAchievements.length ? 'Dados retornados' : 'Indisponível' }}</small>
            </div>
            <div v-if="selectedAchievements.length" class="mt-3 grid grid-cols-2 gap-2">
              <span v-for="achievement in selectedAchievements" :key="achievement.id" class="rounded-lg border border-[#eeeaf4] px-2 py-2 text-[7px] text-[#5a24c7]">{{ achievement.label }}</span>
            </div>
            <p v-else class="mt-3 rounded-lg bg-[#faf9fd] px-2 py-2 text-[7px] text-[#7180a4]">Conquistas não foram retornadas para esta proposta.</p>
          </section>

          <footer data-imudei-visual-id="proposal-dashboard-footer" class="mt-4 flex gap-3 border-t border-[#eeeaf4] pt-4">
            <button
              class="h-9 flex-1 rounded-lg border border-[#d7c8ff] text-[8px] font-bold text-[#5a24c7]"
              type="button"
              @click="open('detail')"
            >
              Abrir detalhes
            </button>
            <button
              v-if="canEdit"
              class="h-9 flex-1 rounded-lg bg-gradient-to-r from-[#5620ce] to-[#7828ec] text-[8px] font-bold text-white"
              type="button"
              @click="open('edit')"
            >
              Editar proposta
            </button>
            <span
              v-else
              class="grid h-9 flex-1 place-items-center rounded-lg bg-[#f5f3f8] px-2 text-center text-[7px] text-[#7180a4]"
            >
              Edição indisponível
            </span>
          </footer>
        </template>

        <div v-else class="grid h-full min-h-[700px] place-items-center text-center">
          <span>
            <i class="i-lucide-panel-right-open mx-auto size-8 text-[#a58add]" />
            <strong class="mt-3 block text-[10px]">Selecione uma proposta</strong>
            <small class="mt-1 block text-[8px] text-[#7180a4]">O painel mostra somente dados reais.</small>
          </span>
        </div>
      </aside>
    </div>

    <p v-if="loading" class="mt-4 text-[9px] text-[#7180a4]">Carregando propostas...</p>
  </main>
</template>
